import React, { useState, useEffect } from 'react';
import HistoryIcon from "@mui/icons-material/History";
import "../../styles/HistoryPatient.css"; 

const BASE_URL = "http://127.0.0.1:8000";
export const HistoryPatientToolConfig = {
  name: "HISTORY",
  tool: "HistoryPatient",
  icon: <HistoryIcon />,
  onClick: (patient) => openHistoryPatient(patient),
};

const formatDate = (isoString) => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const openHistoryPatient = (patient) => {
  const modalRoot = document.createElement('div');
  modalRoot.id = 'history-patient-modal-root';
  document.body.appendChild(modalRoot);
  import('react-dom/client').then(({ createRoot }) => {
    const root = createRoot(modalRoot);
    
    root.render(
      <HistoryPatientModal 
        patient={patient}
        onClose={() => {
          root.unmount();
          document.body.removeChild(modalRoot);
        }}
      />
    );
  });
};

const HistoryPatientModal = ({ patient, onClose }) => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const patientId = patient?.patient_id || patient?.patientId || patient?.id || "";
  const patientName = patient?.patient_name || patient?.patient || patient?.name || "";
  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    
    fetchHistories();
  }, [patientId]);

  // Handle Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Fetch histories from backend
  const fetchHistories = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${BASE_URL}/api/patient-history/list/?patient_id=${encodeURIComponent(patientId)}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch history");
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setHistories(data);
      } else {
        setHistories([]);
      }
    } catch (error) {
      console.error("History fetch error:", error);
      setError("History not loded");
    } finally {
      setLoading(false);
    }
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle image click to open in new tab
  const handleImageClick = (attachmentUrl) => {
    const fullUrl = attachmentUrl.startsWith("http") 
      ? attachmentUrl 
      : `${BASE_URL}${attachmentUrl}`;
    window.open(fullUrl, '_blank');
  };

  // Get subtitle text
  const getSubtitle = () => {
    if (!patientId) return "Patient ID not found";
    return patientName ? `${patientName} · ${patientId}` : patientId;
  };

  // Render loading state
  const renderLoading = () => (
    <div className="history-patient__loading">
      <p>Loading patient history...</p>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div className="history-patient__empty">
      <p className="history-patient__empty-icon">⚠️</p>
      <p className="history-patient__empty-text">{error}</p>
    </div>
  );

  // Render no history state
  const renderNoHistory = () => (
    <div className="history-patient__empty">
      <p className="history-patient__empty-icon">📋</p>
      <p className="history-patient__empty-text">
        History not found for this patient.
      </p>
    </div>
  );

  // Render history cards
  const renderHistories = () => (
    <>
      {histories.map((history, index) => (
        <div key={history.id || index} className="history-patient__card">
          <div className="history-patient__card-header">
            <span className="history-patient__badge">
              #{histories.length - index}
            </span>
            <span className="history-patient__date">
              {formatDate(history.created_at)}
            </span>
          </div>

          <div className="history-patient__text">
            {escapeHtml(history.history_text || "—")}
          </div>

          {history.attachment ? (
  <div className="history-patient__attachment">
    <div className="history-patient__attachment-label">
      📎 Attachment
    </div>

    {history.attachment.toLowerCase().endsWith(".pdf") ? (
      <a
        href={
          history.attachment.startsWith("http")
            ? history.attachment
            : `${BASE_URL}${history.attachment}`
        }
        target="_blank"
        rel="noreferrer"
        className="history-patient__pdf-link"
      >
        📄 View PDF
      </a>
    ) : (
      <img
        src={
          history.attachment.startsWith("http")
            ? history.attachment
            : `${BASE_URL}${history.attachment}`
        }
        alt="attachment"
        className="history-patient__attachment-image"
        onClick={() => handleImageClick(history.attachment)}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    )}
  </div>
) : (
  <div className="history-patient__no-attachment">
    No attachment
  </div>
)}
        </div>
      ))}
    </>
  );

  // No Patient ID case
  if (!patientId) {
    return (
      <div className="history-patient__overlay" onClick={handleOverlayClick}>
        <div className="history-patient__modal">
          <div className="history-patient__header">
            <div>
              <h2 className="history-patient__title">Patient History</h2>
              <p className="history-patient__subtitle">Patient ID not found</p>
            </div>
            <button
              className="history-patient__close-btn"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
          <div className="history-patient__content">
            <div className="history-patient__empty">
              <p className="history-patient__empty-icon">⚠️</p>
              <p className="history-patient__empty-text" style={{ color: "#ef4444" }}>
                Patient ID not found
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-patient__overlay" onClick={handleOverlayClick}>
      <div className="history-patient__modal">
        {/* Header */}
        <div className="history-patient__header">
          <div>
            <h2 className="history-patient__title">Patient History</h2>
            <p className="history-patient__subtitle">{getSubtitle()}</p>
          </div>
          <button
            className="history-patient__close-btn"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="history-patient__content">
          {loading && renderLoading()}
          {error && renderError()}
          {!loading && !error && histories.length === 0 && renderNoHistory()}
          {!loading && !error && histories.length > 0 && renderHistories()}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Helper: Escape HTML
// ─────────────────────────────────────────────────────────
const escapeHtml = (text) => {
  if (!text) return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};